import { ItemAddonType } from '@order-mage/integration-sdk';
import { useMemo } from 'react';
import type {
    StorefrontAddonDef,
    StorefrontAddonMemberRef,
    StorefrontItemBody,
} from '@/api/menu-list/storefront-menu-snapshot.types';
import { useCartStore, getMemberHash, getHashFromItemIdMap, isItemPriceConfigured } from '@/entities/cart';
import { useStorefrontItemsIndex } from '@/entities/product/lib/useStorefrontMenu';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { LocalizedString } from '@/shared/model/Language';

export type VisibleAddonSection = {
    key: string;
    addonDef: Pick<
        StorefrontAddonDef,
        'id' | 'behaviour' | 'minAmount' | 'maxAmount' | 'name' | 'autoJumpToNextSection'
    >;
    members: StorefrontAddonMemberRef[];
};

export const listVisibleAddonSections = (
    currentItem: Pick<StorefrontItemBody, 'addons'> | undefined,
    itemsMap: ReturnType<typeof useStorefrontItemsIndex>,
    getLocalizedValue: (value: LocalizedString) => string,
    getItemPrices: (hash: string) => unknown,
): VisibleAddonSection[] => {
    if (!currentItem?.addons?.length) return [];
    const idToHash = getHashFromItemIdMap(itemsMap);

    const isMemberVisible = (member: StorefrontAddonMemberRef): boolean => {
        if (member.public === false) return false;
        const hash = getMemberHash(member, idToHash) ?? member.hash;
        const menuItem = hash ? itemsMap[hash] : undefined;
        if (!menuItem?.body) return false;
        const hasName = !!getLocalizedValue((menuItem.body.name ?? {}) as LocalizedString);
        if (!hasName) return false;
        if (!isItemPriceConfigured(menuItem.body)) return false;
        return getItemPrices(hash) !== null;
    };

    const buildSection = (addon: StorefrontAddonDef): VisibleAddonSection | null => {
        if (!addon.id) return null;
        const visibleMembers = addon.members.filter(isMemberVisible);
        if (visibleMembers.length === 0) return null;
        return {
            key: addon.id,
            addonDef: {
                id: addon.id,
                behaviour: addon.behaviour,
                minAmount: addon.minAmount,
                maxAmount: addon.maxAmount,
                name: addon.name,
                autoJumpToNextSection: addon.autoJumpToNextSection,
            },
            members: visibleMembers,
        };
    };

    return currentItem.addons.flatMap((addon): VisibleAddonSection[] => {
        if (addon.type === ItemAddonType.GROUP && addon.groupMembers?.length) {
            return addon.groupMembers
                .map(buildSection)
                .filter((section): section is VisibleAddonSection => section !== null);
        }
        const section = buildSection(addon);
        return section ? [section] : [];
    });
};

export const useVisibleAddonSections = (
    currentItem: StorefrontItemBody | undefined,
): VisibleAddonSection[] => {
    const getLocalizedValue = useGetLocalizedValue();
    const getItemPrices = useCartStore((s) => s.getItemPrices);
    const itemsMap = useStorefrontItemsIndex();

    return useMemo(
        () => listVisibleAddonSections(currentItem, itemsMap, getLocalizedValue, getItemPrices),
        [currentItem, itemsMap, getLocalizedValue, getItemPrices],
    );
};
